import { useEffect, useRef, useState } from 'react'

import { MediaGallery } from '@awazevr/design-system.components.media-gallery'
import { breakpoints } from '@awazevr/design-system.utils.react'
import type { SearchCriteria } from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { OptimizelyState } from '@/context/optimizely-decisions-context'
import { usePublicAssetUrl } from '@/utils/helpers'

import { buildTileHref } from './buildTileHref'
import type { ExplorationLocation, ExplorationTile } from './data'
import {
  ExplorationContainer,
  ExplorationHeading,
  ExplorationImage,
  ExplorationLabel,
  ExplorationLink,
  ExplorationTrack,
} from './ExplorationSuggestions.style'
import { trackExplorationInteraction } from './tracking'

export interface ExplorationSuggestionsProps {
  location: ExplorationLocation
  tiles: ExplorationTile[]
  searchCriteria?: SearchCriteria
  optimizelyState: OptimizelyState
}

const HEADINGS: Record<ExplorationLocation, string> = {
  cornwall: 'Destinations in Cornwall',
  st_ives: 'Destinations near St Ives',
}

interface TileMeta {
  tile: ExplorationTile
  position: number
}

interface ExplorationTileItemProps {
  tile: ExplorationTile
  index: number
  location: ExplorationLocation
  searchCriteria?: SearchCriteria
  optimizelyState: OptimizelyState
  registerRef: (element: HTMLAnchorElement | null) => void
}

const ExplorationTileItem = ({
  tile,
  index,
  location,
  searchCriteria,
  optimizelyState,
  registerRef,
}: ExplorationTileItemProps): JSX.Element => {
  const imageUrl = usePublicAssetUrl(tile.imageSrc)

  return (
    <ExplorationLink
      ref={registerRef}
      data-index={index}
      href={buildTileHref(tile, searchCriteria)}
      aria-label={tile.regionName}
      data-testid={`exploration-tile-${tile.placesId}`}
      draggable={false}
      onClick={() =>
        trackExplorationInteraction(
          location,
          tile,
          index + 1,
          'click',
          optimizelyState,
        )
      }
    >
      <ExplorationImage
        src={imageUrl}
        alt={tile.regionName}
        loading="lazy"
        draggable={false}
        width={238}
        height={124}
      />
      <ExplorationLabel>{tile.regionName}</ExplorationLabel>
    </ExplorationLink>
  )
}

export const ExplorationSuggestions = ({
  location,
  tiles,
  searchCriteria,
  optimizelyState,
}: ExplorationSuggestionsProps): JSX.Element | null => {
  const tileMetaRef = useRef<Map<Element, TileMeta>>(new Map())
  const seenRef = useRef<Set<Element>>(new Set())

  const [ready, setReady] = useState(false)

  useEffect(() => {
    const fallback = globalThis.setTimeout(() => setReady(true), 600)
    return () => globalThis.clearTimeout(fallback)
  }, [])

  useEffect(() => {
    seenRef.current = new Set()
  }, [location, tiles])

  useEffect(() => {
    // Only observe once the carousel is actually shown (ready) so impressions
    // are not recorded while it is still hidden (opacity 0) pre-init.
    if (!ready || typeof IntersectionObserver === 'undefined') {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return
          }

          const meta = tileMetaRef.current.get(entry.target)

          if (!meta || seenRef.current.has(entry.target)) {
            return
          }

          seenRef.current.add(entry.target)
          trackExplorationInteraction(
            location,
            meta.tile,
            meta.position,
            'impression',
            optimizelyState,
          )
          observer.unobserve(entry.target)
        })
      },
      { threshold: 0.5 },
    )

    // Drop any elements no longer in the DOM (e.g. after a variant switch).
    tileMetaRef.current.forEach((_meta, element) => {
      if (!element.isConnected) {
        tileMetaRef.current.delete(element)
      }
    })

    tileMetaRef.current.forEach((_meta, element) => observer.observe(element))

    return () => observer.disconnect()
  }, [ready, location, tiles, optimizelyState])

  if (tiles.length === 0) {
    return null
  }

  return (
    <ExplorationContainer
      aria-label={HEADINGS[location]}
      data-testid="exploration-suggestions"
    >
      <ExplorationHeading>{HEADINGS[location]}</ExplorationHeading>
      <ExplorationTrack $ready={ready}>
        <MediaGallery
          onSwiperInit={() => setReady(true)}
          swiperConfig={{
            slidesPerView: 'auto',
            spaceBetween: 12,
            grabCursor: true,
            speed: 250,
            navigation: false,
            preventClicks: true,
            preventClicksPropagation: true,
            watchOverflow: true,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            breakpoints: {
              [breakpoints[0]]: {
                slidesOffsetBefore: 20,
                slidesOffsetAfter: 20,
              },
            },
          }}
        >
          {tiles.map((tile, index) => (
            <ExplorationTileItem
              key={tile.placesId}
              tile={tile}
              index={index}
              location={location}
              searchCriteria={searchCriteria}
              optimizelyState={optimizelyState}
              registerRef={(element) => {
                if (element) {
                  tileMetaRef.current.set(element, {
                    tile,
                    position: index + 1,
                  })
                }
              }}
            />
          ))}
        </MediaGallery>
      </ExplorationTrack>
    </ExplorationContainer>
  )
}
